import Button from '@mui/material/Button'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import React from 'react'
import {
  EXPANDER_COLUMN_MIN_WIDTH,
  HEADER_CELLS,
  HEADER_CELL_MIN_WIDTHS,
} from '../constants'
import {
  getPendingCancellationChipColor,
  getSubscriptionStatusChipColor,
  getTransactionStatusChipColor,
} from '../helpers'
import {
  AdminSubscriptionTableRow,
  formatAddress,
  formatCurrency,
} from '../utils'
import { CustomerWorkbenchModal } from '../../CustomerWorkbench/CustomerWorkbenchModal'
import { CustomerContactModal } from './CustomerContactModal'
import { SubscriptionTransactions } from './SubscriptionTransactions'

interface Props {
  isExpanded: boolean
  onToggleRow: (subscriptionId: string) => void
  row: AdminSubscriptionTableRow
}

export const SubscriptionRow: React.FC<Props> = ({
  isExpanded,
  onToggleRow,
  row,
}) => {
  const [contactModalOpen, setContactModalOpen] = React.useState(false)
  const [workbenchOpen, setWorkbenchOpen] = React.useState(false)
  const formattedAddress = formatAddress(row)
  const hasAddress = formattedAddress !== '—'
  const cellSx = React.useMemo(
    () => ({
      whiteSpace: 'nowrap',
      minWidth: 140,
      verticalAlign: 'top',
    }),
    []
  )

  return (
    <React.Fragment>
      <TableRow hover>
        <TableCell
          padding="checkbox"
          sx={{
            ...cellSx,
            minWidth: EXPANDER_COLUMN_MIN_WIDTH,
            width: EXPANDER_COLUMN_MIN_WIDTH,
            verticalAlign: 'middle',
            textAlign: 'center',
          }}
        >
          <IconButton
            size="small"
            onClick={() => onToggleRow(row.subscriptionId)}
            aria-label={
              isExpanded
                ? `Collapse transactions for ${row.subscriptionId}`
                : `Expand transactions for ${row.subscriptionId}`
            }
          >
            {isExpanded ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell
          sx={{
            ...cellSx,
            minWidth: HEADER_CELL_MIN_WIDTHS.customerName,
          }}
        >
          <Stack spacing={0.5}>
            <Button
              size="small"
              variant="text"
              onClick={() => setWorkbenchOpen(true)}
              sx={{
                px: 0,
                justifyContent: 'flex-start',
                whiteSpace: 'nowrap',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {row.customerName}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {`Customer #${row.customerProfileId}`}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell
          sx={{ ...cellSx, minWidth: HEADER_CELL_MIN_WIDTHS.phoneNumber }}
        >
          {row.phoneNumber || '—'}
        </TableCell>
        <TableCell sx={{ ...cellSx, minWidth: HEADER_CELL_MIN_WIDTHS.email }}>
          {row.email || '—'}
        </TableCell>
        <TableCell sx={{ ...cellSx, minWidth: HEADER_CELL_MIN_WIDTHS.address }}>
          {hasAddress ? (
            <Button
              size="small"
              variant="text"
              onClick={() => setContactModalOpen(true)}
              sx={{
                px: 0,
                justifyContent: 'flex-start',
                textAlign: 'left',
                whiteSpace: 'nowrap',
              }}
            >
              {formattedAddress}
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              —
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ ...cellSx, minWidth: HEADER_CELL_MIN_WIDTHS.unitLabel }}>
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
              {row.unitLabel}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="nowrap">
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Unit {row.unitNumber}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {row.unitType}
              </Typography>
            </Stack>
          </Stack>
        </TableCell>
        <TableCell sx={{ ...cellSx, minWidth: HEADER_CELL_MIN_WIDTHS.startDate }}>
          <Stack spacing={0.25}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              {row.startDate}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Created {row.createdAt}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell
          align="right"
          sx={{ ...cellSx, minWidth: HEADER_CELL_MIN_WIDTHS.amount }}
        >
          {formatCurrency(row.amount)}
        </TableCell>
        <TableCell
          sx={{ ...cellSx, minWidth: HEADER_CELL_MIN_WIDTHS.paymentType }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="nowrap">
            <Chip size="small" variant="outlined" label={row.paymentType} />
          </Stack>
        </TableCell>
        <TableCell
          sx={{
            ...cellSx,
            minWidth: HEADER_CELL_MIN_WIDTHS.latestTransactionStatus,
          }}
        >
          <Stack spacing={0.5}>
            <Chip
              size="small"
              color={getTransactionStatusChipColor(row.latestTransactionStatus)}
              label={row.latestTransactionStatus}
              sx={{ width: 'fit-content' }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {row.latestTransactionTimestamp
                ? `Last activity ${new Date(
                    row.latestTransactionTimestamp
                  ).toLocaleDateString()}`
                : 'No linked charges yet'}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell
          align="center"
          sx={{
            ...cellSx,
            minWidth: HEADER_CELL_MIN_WIDTHS.transactionCount,
          }}
        >
          <Chip
            size="small"
            label={row.transactionCount}
            color={row.transactionCount > 0 ? 'primary' : 'default'}
          />
        </TableCell>
        <TableCell sx={{ ...cellSx, minWidth: HEADER_CELL_MIN_WIDTHS.status }}>
          <Stack direction="row" spacing={1} flexWrap="nowrap">
            <Chip
              size="small"
              label={row.status}
              color={getSubscriptionStatusChipColor(row.status)}
            />
            {row.pendingCancellationLabel ? (
              <Chip
                size="small"
                label={row.pendingCancellationLabel}
                color={getPendingCancellationChipColor(
                  row.pendingCancellationLabel
                )}
                variant="outlined"
              />
            ) : null}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={HEADER_CELLS.length + 1}
          sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box>
              <SubscriptionTransactions row={row} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <CustomerContactModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        row={row}
      />
      <CustomerWorkbenchModal
        open={workbenchOpen}
        onClose={() => setWorkbenchOpen(false)}
        customerProfileId={row.customerProfileId}
        customerName={row.customerName}
      />
    </React.Fragment>
  )
}
